/**
 * **DISPOSABLE.** Busha probe. Removable in one commit once the two questions below are
 * answered: this page, its nav entry, its route in `App.tsx`, `AdminApi.bushaProbe`, and the
 * Busha block in `contracts/pending.ts`. Nothing else references any of them.
 *
 * ## The two questions
 *
 * 1. **What does `GET /v1/addresses/{code}` return, and is it durable?** Busha's specification
 *    defines only 4xx and 5xx responses for that endpoint, so every field name in the API's
 *    Busha adapter is currently a guess. The probe calls it **twice for the same customer** on
 *    purpose: two identical addresses mean the address is durable per customer and can be
 *    stored, and two different addresses mean it mints one per call and the deposit model has
 *    to change before any customer is handed one. That comparison is the line this page prints
 *    above the raw output.
 *
 * 2. **Is `pay_out.type: "internal"` accepted from one customer profile to another?** Their
 *    only elaboration of that enum member says "the owning business profile", and `sweep` —
 *    the one documented master-to-sub mechanism — is explicitly rejected on public API-key
 *    requests. If internal is business-only, per-customer profiles cannot move coin between
 *    customers at all and GenEx-to-GenEx sends stay on the internal ledger.
 *
 * ## What this page does with the key
 *
 * It holds it in component state for the length of one run and posts it in the request
 * **body**. Never a query string, never a path segment, never a header this page sets: those
 * are the three places a secret ends up in an access log, a browser history entry or a
 * referrer, and none of them is undone by deleting this page afterwards. It is never written
 * to `localStorage` or `sessionStorage`, and it is cleared when the page unmounts. The server
 * neither stores nor logs it, and redacts it out of every vendor body before replying.
 */
import { useEffect, useMemo, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { readBushaProbe } from '../contracts/pending.js';
import type { PendingBushaProbeResponse, PendingBushaProbeStep } from '../contracts/pending.js';
import { Button, Card, CopyButton, ErrorBox, Field, Input } from '../components/ui.js';

/** Shown beside the field so nobody has to guess which host a blank means. */
const DEFAULT_BASE_URL = 'https://api.sandbox.busha.so';

/** The two calls whose answers are compared. Matched on path, not on position in the list. */
const ADDRESS_PATH_SUFFIX = '/v1/addresses/BTC';

/** The step that carries question two. */
const TRANSFER_PATH_SUFFIX = '/v1/transfers';

/** One scrollable, monospaced block. Inline because this page is temporary and owns no CSS. */
const OUTPUT_STYLE = {
  maxHeight: '58vh',
  overflow: 'auto',
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
  margin: 0,
  fontSize: '12px',
  lineHeight: 1.55,
};

export function BushaProbePage(): JSX.Element {
  const { api } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PendingBushaProbeResponse | null>(null);

  // Cleared when the operator leaves. The state would be discarded anyway; saying so
  // explicitly is the point, because the whole handling rule for this value is that it lives
  // for one run and no longer.
  useEffect(() => () => setApiKey(''), []);

  const transcript = useMemo(() => (result === null ? '' : transcriptOf(result)), [result]);

  async function run(): Promise<void> {
    const key = apiKey.trim();
    if (key === '') {
      setError('Paste a Busha API key first.');
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const trimmedBase = baseUrl.trim();
      const raw = await api.bushaProbe({
        apiKey: key,
        ...(trimmedBase === '' ? {} : { baseUrl: trimmedBase }),
      });
      const parsed = readBushaProbe(raw);
      if (parsed === null) {
        setError('The probe ran but the response could not be read. Nothing was stored.');
        return;
      }
      setResult(parsed);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div>
      <header className="page-head">
        <h1 className="page-head__title">Busha probe</h1>
        <p className="page-head__sub">
          A temporary diagnostic. It runs seven calls against Busha with a key you paste here,
          to settle two things their documentation does not answer: whether a customer&apos;s
          deposit address is the same every time it is asked for, and whether one customer can
          be paid directly from another. Delete this page once both are answered.
        </p>
      </header>

      <Card title="Run the probe">
        <p className="muted" style={{ maxWidth: '68ch' }}>
          The key is used for this one run and then dropped. It is sent in the request body, it
          is not saved to provider logins or anywhere else, it is kept out of the logs and the
          audit trail, and the server blanks it out of every reply Busha sends back. It is
          cleared from this screen when you navigate away.
        </p>

        <div style={{ display: 'grid', gap: '12px', maxWidth: '520px', marginTop: '16px' }}>
          <Field label="Busha API key" htmlFor="busha-probe-key">
            <Input
              id="busha-probe-key"
              type="password"
              value={apiKey}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="sk_..."
              onChange={(e) => setApiKey(e.target.value)}
            />
          </Field>
          <Field label="Base URL (optional)" htmlFor="busha-probe-base">
            <Input
              id="busha-probe-base"
              type="text"
              value={baseUrl}
              autoComplete="off"
              spellCheck={false}
              placeholder={DEFAULT_BASE_URL}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </Field>
          <div>
            <Button variant="primary" loading={running} onClick={() => void run()}>
              {running ? 'Running seven calls…' : 'Run probe'}
            </Button>
          </div>
        </div>

        {error !== null && (
          <div style={{ marginTop: '16px' }}>
            <ErrorBox message={error} />
          </div>
        )}
      </Card>

      {result !== null && (
        <div style={{ marginTop: '20px' }}>
          <Card
            title="What the run answered"
            actions={<CopyButton value={transcript} label="the whole probe output" />}
          >
            {/* Question one, in one line, above the raw output. Steps 3 and 4 are the same
                call twice on purpose and this is the only reason the probe makes both. */}
            <p style={{ fontWeight: 600 }}>{addressVerdictLine(result.steps)}</p>
            <p style={{ fontWeight: 600 }}>{transferVerdictLine(result.steps)}</p>
            <p className="muted">
              Ran against {result.baseUrl === '' ? DEFAULT_BASE_URL : result.baseUrl}
              {result.ranAt === null ? '' : ` at ${result.ranAt}`}. A refusal below is a result,
              not a failure: it tells us which shape to build.
            </p>
            <pre className="mono" style={OUTPUT_STYLE}>
              {transcript}
            </pre>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading the answers out of the steps
// ─────────────────────────────────────────────────────────────────────────────

/** The two `/v1/addresses/BTC` calls, in the order the server made them. */
function addressSteps(steps: readonly PendingBushaProbeStep[]): readonly PendingBushaProbeStep[] {
  return steps.filter((step) => step.path.endsWith(ADDRESS_PATH_SUFFIX));
}

/**
 * Question one, stated plainly.
 *
 * Only two successful calls that both produced an address can answer it. Two failures do not
 * mean "not durable", they mean the question was never reached, and reporting that as a "no"
 * would send somebody off to rebuild the deposit model on the strength of a 401.
 */
function addressVerdictLine(steps: readonly PendingBushaProbeStep[]): string {
  const [first, second] = addressSteps(steps);
  if (first === undefined || second === undefined) {
    return 'Deposit address: not answered. The probe did not get to make both address calls.';
  }
  if (!isOk(first.status) || !isOk(second.status)) {
    return `Deposit address: not answered. The two calls came back ${String(first.status)} and ${String(second.status)}, so there is nothing to compare.`;
  }
  const a = addressOf(first.body);
  const b = addressOf(second.body);
  if (a === null || b === null) {
    return 'Deposit address: not answered. Both calls succeeded but no address field could be found in the bodies below. Read them and correct the adapter by hand.';
  }
  return a === b
    ? 'Deposit address: the two calls returned the SAME address, so it is durable per customer and can be stored and reused.'
    : 'Deposit address: the two calls returned DIFFERENT addresses, so Busha mints one per call and the deposit model has to change before any customer is handed one.';
}

/**
 * Question two, stated plainly.
 *
 * A 4xx is a real answer: the vendor considered the request and refused it. A 5xx or a
 * transport failure is not, because treating an outage as a refusal would close off a
 * capability on no evidence.
 */
function transferVerdictLine(steps: readonly PendingBushaProbeStep[]): string {
  const step = steps.find((entry) => entry.path.endsWith(TRANSFER_PATH_SUFFIX));
  if (step === undefined || step.status === 0) {
    return 'Customer to customer transfer: not answered. The call was never made or never got there.';
  }
  if (isOk(step.status)) {
    return 'Customer to customer transfer: ACCEPTED. pay_out.type "internal" works between two customer profiles, so coin can move between customers at the vendor.';
  }
  if (step.status >= 400 && step.status < 500) {
    return `Customer to customer transfer: REFUSED with ${String(step.status)}. Internal payouts look business-only, so GenEx to GenEx sends stay on our own ledger. Read the body below for the reason given.`;
  }
  return `Customer to customer transfer: not answered. Busha replied ${String(step.status)}, which is its own failure rather than a verdict.`;
}

function isOk(status: number): boolean {
  return status >= 200 && status < 300;
}

/**
 * The address string a `/v1/addresses` body carried, wherever it put it.
 *
 * Tries the documented-looking shape first, then any string field whose name contains
 * "address", because the entire reason this probe exists is that the shape is undocumented and
 * a comparison that silently finds nothing is worse than one that guesses and says so.
 */
function addressOf(body: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  const data = parsed['data'] ?? parsed;
  const record = Array.isArray(data) ? data[0] : data;
  if (!isRecord(record)) return null;

  const direct = record['address'];
  if (typeof direct === 'string' && direct.trim() !== '') return direct.trim();

  for (const [key, value] of Object.entries(record)) {
    if (key.toLowerCase().includes('address') && typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Every step in order, as one block of text.
 *
 * The same text the copy button hands over, so what somebody pastes into a chat is exactly
 * what they were looking at rather than a second rendering of it.
 */
function transcriptOf(result: PendingBushaProbeResponse): string {
  const header = [
    `Busha probe against ${result.baseUrl === '' ? DEFAULT_BASE_URL : result.baseUrl}`,
    result.ranAt === null ? null : `Ran at ${result.ranAt}`,
    'The API key was redacted by the server before this was sent.',
  ].filter((line): line is string => line !== null);

  const blocks = result.steps.map((step, index) => {
    const method = step.method === '' ? '' : `${step.method} `;
    const status = step.status === 0 ? 'no response' : `HTTP ${String(step.status)}`;
    const lines = [
      `=== ${String(index + 1)}. ${step.label} ===`,
      `${method}${step.path}`,
      status,
      step.error === null ? null : `not run: ${step.error}`,
      step.body === '' ? '(empty body)' : step.body,
    ];
    return lines.filter((line): line is string => line !== null).join('\n');
  });

  return [header.join('\n'), ...blocks].join('\n\n');
}
